<template>
  <section class="poll-voting" v-if="details" :class="pubtheme" id="poll-voting">
    <h2 class="poll-voting__title">{{ details.name }}</h2>
    <template v-if="details.ended">
      <p>Denne avstemningen er nå avsluttet</p>
    </template>
    <template v-else>
      <ul class="poll-voting__list">
        <li
          class="poll-voting__list-item poll-voting__list-item--check"
          :class="{ 'poll-voting__list-item--active': opt.id === option }"
          v-for="(opt, key) in details.options"
          :key="key"
          @click="setOption(opt.id)"
        >
          {{ opt.title }}
        </li>
      </ul>

      <section class="poll-voting__bottom">
        <div v-if="loggedIn">
          <span class="poll-voting__submit" :class="{ 'poll-voting__submit--validating': validating || waiting }" @click="vote()" v-if="option || state">
            <template>Avgi din stemme</template>
          </span>
        </div>
        <div v-else>
          <span class="poll-voting__submit" :class="{ 'poll-voting__submit--validating': validating }">
            <template>Logg inn for å stemme</template>
          </span>
        </div>

        <template v-if="state && details.validation === 'email'">
          <div class="poll-voting__popup">
            <validate-animation :view="state"></validate-animation>
            <div class="poll-voting__input poll-voting__email" :class="{ 'poll-voting__input--show': state === 'email' }">
              <p class="poll-voting__input-title">Bare én liten ting...</p>
              <p class="poll-voting__input-text">Vi trenger din e-post for å registrere din stemme</p>
              <input class="poll-voting__input-input" placeholder="E-post" ref="email" v-model="email" type="text" @keyup.enter="vote()" />
              <span class="poll-voting__input-error" v-if="fields.email.state">{{ fields.email.message }}</span>
              <span class="poll-voting__input-btn" :class="{ 'poll-voting__input-btn--disable': fields.email.loading }" @click="vote()">
                <template v-if="!fields.email.loading">Send</template>
                <span v-else class="poll-voting__input-btn-spinn">
                  <div class="lds-ring">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                </span>
              </span>
            </div>
            <div class="poll-voting__input poll-voting__validating" :class="{ 'poll-voting__input--show': state === 'validation' }">
              <p class="poll-voting__input-title">...nesten i mål!</p>
              <p class="poll-voting__input-text">Skriv inn valideringskode sendt til din e-post.</p>

              <input class="poll-voting__input-input" placeholder="Kode" ref="code" v-model="code" type="text" @keyup.enter="vote()" />
              <span class="poll-voting__input-error" v-if="fields.code.state">{{ fields.code.message }}</span>
              <span class="break"></span>
              <span class="poll-voting__input-btn" :class="{ 'poll-voting__input-btn--disable': fields.code.loading }" @click="vote()">
                <template v-if="!fields.code.loading">Valider</template>
                <span v-else class="poll-voting__input-btn-spinn">
                  <div class="lds-ring">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                </span>
              </span>
            </div>
            <div class="poll-voting__input poll-voting__thanks" :class="{ 'poll-voting__input--show': state === 'thanks' }">
              <p class="poll-voting__input-title">Takk for din stemme!</p>
              <span class="poll-voting__input-btn" @click="close()">Lukk</span>
            </div>
            <div class="poll-voting__input poll-voting__no-more" :class="{ 'poll-voting__input--show': state === 'no-more' }">
              <p class="poll-voting__input-title">Hekkan!</p>
              <p class="poll-voting__input-text">Din stemme er allerede gitt</p>
              <span class="poll-voting__input-btn" @click="close()">Lukk</span>
            </div>
            <div class="poll-voting__input poll-voting__ended" :class="{ 'poll-voting__input--show': state === 'ended' }">
              <p class="poll-voting__input-title">Hekkan!</p>
              <p class="poll-voting__input-text">Denne avstemningen er nå avsluttet...</p>
              <span class="poll-voting__input-btn" @click="close()">Lukk</span>
            </div>
          </div>
          <span class="poll-voting__popup-close" @click="close()"></span>
        </template>
        <div v-if="details.validation === 'ip'">
          <p class="poll-voting__input-text" v-show="state === 'ip-voted'">Takk for din stemme!</p>
          <p class="poll-voting__input-text" v-show="state === 'ip-no-more'">Din stemme er allerede gitt</p>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import { find, map, mapValues, orderBy } from 'lodash-es';
import { GET_POLLS } from './store';
import { mapGetters } from 'vuex';
import { firebase, requestVote, validateVote, validateVoteUrlCode } from './utils/firebase-methods';
import validateAnimation from './components/validateAnimation';
import sha3 from 'crypto-js/sha3';
import { validate, extend } from 'vee-validate';
import { required, email, digits } from 'vee-validate/dist/rules';
extend('required', required);
extend('email', email);
extend('digits', digits);
// Register it globally
export default {
  name: 'poll',
  props: {
    poll: {
      type: String,
    },
  },
  data() {
    return {
      pub: null,
      theme: 'default',
      loggedIn: true,
      poll_id: null,
      state: null,
      voted: false,
      option: null,
      code: null,
      email: null,
      waiting: false,
      fields: {
        email: {
          state: false,
          loading: false,
          message: 'Er dette gyldig e-post? Prøv igjen!',
        },
        code: {
          focus: false,
          state: false,
          loading: false,
          message: 'Ble dette riktig da? Prøv igjen!',
        },
      },
    };
  },
  computed: {
    pubtheme() {
      return this.pub + ' ' + this.theme;
    },
    ...mapGetters({
      getPolls: GET_POLLS,
    }),
    details() {
      return mapValues(find(this.getPolls, ['poll', this.poll_id]), (elem, key) => {
        if (key === 'options')
          return orderBy(
            map(elem, (e, id) => {
              return { ...e, id: id };
            }),
            ['pos'],
            ['asc']
          );
        return elem;
      });
    },
    validating() {
      return ['email', 'validation', 'no-more', 'thanks', 'ended'].indexOf(this.state) >= 0;
    },
    options() {
      return {
        type: 'poll',
        poll_id: this.poll_id,
        poll_val: this.option,
        email: this.email,
        url: window.location.href,
      };
    },
  },
  methods: {
    onStorageUpdate(event) {
      if (event.key === 'userSelectedTheme') {
        this.theme = event.newValue;
      }
    },
    setOption(option) {
      // Set the selected option to added option
      this.option = option;
    },
    close() {
      // Reset values
      this.state = null;
      this.option = null;
      this.email = null;
      this.code = null;
      this.waiting = false;
      this.fields.code.state = false;
      this.fields.code.loading = false;
      this.fields.email.state = false;
      this.fields.email.loading = false;
    },
    resetUrl() {
      if (typeof window.history.replaceState === 'function') {
        window.history.replaceState(null, document.title, window.location.origin + window.location.pathname);
      }
    },
    async validateEmail() {
      if (!this.email) {
        this.state = 'email';
      }
      this.email = this.email.trim();
      const validateEmail = await validate(this.email, 'required|email');
      this.fields.email.state = !validateEmail.valid && this.email;
      return validateEmail.valid;
    },
    async vote() {
      // If validation email is required
      if (this.details.validation === 'ip') {
        this.waiting = true;
        const status = await requestVote(this.options);
        console.log(status);
        if (status.status) {
          this.state = 'ip-voted';
          this.waiting = false;
        } else if (!status.status && status.reason === 'VOTED') {
          this.state = 'ip-no-more';
          this.waiting = false;
        }
        this.option = null;
        this.waiting = false;
      } else if (this.details.validation === 'email') {
        // If state is email
        if (!this.state || this.state === 'email') {
          // Check if email is
          const validEmail = await this.validateEmail();
          if (validEmail) {
            this.fields.email.loading = true;
            const status = await requestVote(this.options);
            this.fields.email.loading = false;
            if (status.status) {
              this.state = 'validation';
              firebase
                .firestore()
                .collection('votes-approved')
                .doc(sha3(this.email + '|' + this.poll_id).toString())
                .onSnapshot((snap) => {
                  if (snap.exists) {
                    this.state = 'thanks';
                    this.fields.code.state = true;
                    this.fields.code.loading = false;
                  }
                });
            } else {
              if (status.reason === 'VOTED') this.state = 'no-more';
              else if (status.reason === 'ENDED') this.state = 'ended';
            }
          }
        } else if (this.state === 'validation') {
          const code = parseInt(this.code);
          const validateCode = await validate(code, 'required|digits:6');
          this.fields.email.state = !validateCode.valid;
          if (validateCode.valid) {
            this.fields.code.state = false;
            this.fields.code.loading = true;
            const status = await validateVote({
              ...this.options,
              code: code,
            });
            if (status) {
              this.state = 'thanks';
            }
            this.fields.code.state = true;
            this.fields.code.loading = false;
          }
        }
      }
    },
  },
  async mounted() {
    this.poll_id = this.$root.poll;
    if (window.location.search.indexOf('validate-vote=') >= 0) {
      const url = new URLSearchParams(window.location.search);
      const code = url.get('validate-vote').split(' ').join('+');
      this.state = 'validation';
      this.fields.code.loading = true;
      this.resetUrl();
      const status = await validateVoteUrlCode(code);
      if (status) {
        this.state = 'thanks';
      }
      this.fields.code.state = true;
      this.fields.code.loading = false;
    }
    if (window.localStorage.getItem('userSelectedTheme')) {
      this.theme = window.localStorage.getItem('userSelectedTheme');
    }
    if (window.location.host === 'www.byas.no') {
      this.pub = 'pub-byas';
    }
    if (window.location.host === 'byas.web.schibsted.media') {
      this.pub = 'pub-byas';
    }
    if (window.sessionStorage['hasSession-cache'] != undefined) {
      if (JSON.parse(window.sessionStorage['hasSession-cache']).value.userId) {
        this.loggedIn = true;
      }
    }
    window.addEventListener('storage', this.onStorageUpdate);
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.onStorageUpdate);
  },
  components: {
    validateAnimation,
  },
  watch: {
    state() {
      if (this.state === 'email') {
        window.setTimeout(() => {
          this.$refs['email'].focus();
        }, 200);
      } else if (this.state === 'validation') {
        window.setTimeout(() => {
          this.$refs['code'].focus();
        }, 200);
      }
    },
    option() {
      if (this.option) {
        if (this.state) {
          this.close();
        }
        this.state = null;
      }
    },
  },
};
</script>

<style lang="scss">
@import './styles/settings';
@import './styles/reset';
.poll-voting {
  &.pub-byas {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
      'Segoe UI Symbol' !important;
    &.default {
      color: get-color(byas-white-text);

      .poll-voting {
        &__submit {
          background: get-color(byas-primary);
          color: get-color(light-grey);
        }
        &__input-text {
          color: get-color(light-grey);
        }
        &__list {
          &-item {
            &--check {
              &:after {
                background: get-color(byas-primary);
              }
            }
          }
        }
      }
    }
    &.alternative {
      .poll-voting {
        &__submit {
          background: get-color(byas-primary);
        }
        &__list {
          &-item {
            &--check {
              &:after {
                background: get-color(byas-primary);
              }
            }
          }
        }
      }
    }
  }
  $block: &;
  display: flex;
  flex-flow: column;
  width: 100%;
  font-family: 'Roboto';
  width: 100%;
  max-width: 980px;
  &__title {
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 1rem;
  }
  &__list {
    display: flex;
    flex-flow: row wrap;
    list-style: none;
    padding-left: 0px !important;
    &-item {
      $item: &;
      width: 100%;
      cursor: pointer;
      user-select: none;
      &--check {
        padding: 0.35rem 0 0.35rem 2rem;
        position: relative;
        font-size: 1.08rem;
        &:before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          display: block;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          border: 2px solid get-color(black);
        }
        &:after {
          content: '';
          position: absolute;
          left: 6px;
          top: 50%;
          transform: translateY(-50%);
          display: block;
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: get-color(primary);
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }
        &#{$item}--active:after {
          opacity: 1;
        }
      }
      &--box {
        text-align: center;
        padding: 0.5rem 1rem;
        margin-bottom: 1rem;
        background: #f3f2f2;
        border-radius: 4px;
        transition: background 0.3s ease-in-out, color 0.3s ease-in-out;
        @mixin items($size) {
          width: calc(#{100% / $size} - #{(1rem * ($size - 1)) / $size});
          margin-right: 1rem;
          &:nth-of-type(#{$size}n + #{$size}) {
            margin-right: 0;
          }
        }
        @include breakpoint(medium only) {
          @include items(2);
        }
        @include breakpoint(medium-large only) {
          @include items(3);
        }
        @include breakpoint(large) {
          @include items(4);
        }
        &#{$item}--active,
        &:hover {
          background: get-color(primary);
          color: get-color(light-grey);
        }
      }
    }
  }
  &__submit {
    cursor: pointer;
    display: inline-flex;
    font-size: 1rem;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    text-transform: uppercase;
    border-radius: 4px;
    background: get-color(primary);
    color: get-color(light-grey);
    &--validating {
      opacity: .5;
      color: get-color(light-grey);
      cursor: not-allowed;
      pointer-events: none;
    }
  }
  &__bottom {
    display: inline-flex;
    position: relative;
    flex-direction: column;
    align-items: flex-start;
  }
  &__popup {
    position: absolute;
    z-index: 9999999;
    width: 230px;
    left: 0;
    bottom: 100%;
    background: get-color(light-grey);
    border-radius: 4px;
    border: 1px solid get-color(popup);
    box-shadow: 0 2px 4px -1px rgba(get-color(popup), 0.2), 0 4px 5px 0 rgba(get-color(popup), 0.14), 0 1px 10px 0 rgba(get-color(popup), 0.12);
    @include breakpoint(medium) {
      width: 300px;
    }
    &:after {
      $color: '%23' + str-slice('#{get-color(popup)}', 2, -1);
      content: '';
      height: 20px;
      width: 20px;
      display: block;
      position: absolute;
      left: 20px;
      bottom: -20px;
      transform: rotate(-180deg);
      background-size: contain;
      background-repeat: no-repeat;
      background-position: 50% 10px;
      background-image: url("data:image/svg+xml,%3Csvg width='15' height='11' viewBox='0 0 15 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.50465 1.12512C6.30029 0.229354 7.69962 0.229353 8.49526 1.12511L13.4924 6.75106C14.6382 8.04102 13.7225 10.0792 11.9971 10.0792H2.00281C0.277463 10.0792 -0.638282 8.04103 0.507505 6.75106L5.50465 1.12512Z' fill='#{$color}' /%3E%3C/svg%3E%0A");
    }
    &-close {
      position: fixed;
      z-index: 999999;
      @include position-corner();
    }
  }
  &__input {
    display: flex;
    flex-flow: column;
    text-align: center;
    padding: 1rem;
    @include fadeInOut();
    &--show {
      @include fadeInOut(1);
    }
    &-title {
      font-size: 1rem;
      font-weight: 700;
      margin-bottom: 1rem;
      font-family: 'Source Sans Pro', sans-serif;
      @include breakpoint(medium) {
        font-size: 1.5rem;
      }
    }
    &-text {
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5;
      margin-bottom: 1rem;
      color: get-color(black);
    }
    &-input {
      display: flex;
      width: 100%;
      font-size: 1rem;
      padding: 0.5rem;
      border-radius: 4px;
      border: 1px solid get-color(grey-txt);
      margin-bottom: 1.5rem;
    }
    &-error {
      font-size: 0.8rem;
      height: 1rem;
      font-weight: 100;
      display: flex;
      margin-top: -1rem;
      justify-content: center;
      color: get-color(error);
    }
    &-error + #{&}-btn {
      margin-top: 1rem;
    }
    &-btn {
      display: inline-flex;
      background: get-color(byas-primary);
      color: #fff;
      padding: 0.5rem;
      margin-top: 0rem;
      border-radius: 4px;
      cursor: pointer;
      &--disable {
        cursor: initial;
      }
      &-spinn {
        height: 1rem;
        width: 1rem;
      }
    }
  }
}
/*
.pub-byas .poll-voting {
  &__list-item--box#{&}__list-item--active,
  &__list-item--box:hover {
    background: get-color(byas-primary);
    color: get-color(light-grey);
  }
  &__list-item--check {
    &:before {
      border-color: get-color(byas-black);
    }
    &:after {
      background: get-color(byas-primary);
    }
  }
  &__submit {
    background: get-color(byas-primary);
    color: get-color(light-grey);
    &--validating {
      background: get-color(light-grey);
      color: get-color(byas-primary);
      .lds-ring div {
        border-color: get-color(byas-primary) transparent transparent transparent;
      }
    }
  }
}*/
</style>
